import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth as useClerkAuth, useClerk, useUser } from '@clerk/clerk-react';
import { AuthContext, AuthContextType } from './AuthContext';
import { AuthState } from '@/types/auth';
import { toast } from 'sonner';
import { useLanguage } from '@/hooks/use-language';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { t, language } = useLanguage();
  const { isLoaded: authLoaded, isSignedIn, signOut: clerkSignOut } = useClerkAuth();
  const { isLoaded: userLoaded, user: clerkUser } = useUser();
  const { openSignIn, openSignUp } = useClerk();
  const supabase = useSupabase();

  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const wasSignedIn = useRef(false);
  const hasShownInitialLoginToast = useRef(false);
  const [adminOverride, setAdminOverride] = useState<boolean>(false);

  useEffect(() => {
    if (!clerkUser) {
      setAdminOverride(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        console.debug('AuthProvider: checking admin override for', clerkUser.id);
        const { data, error } = await supabase
          .from('clerk_admin_overrides')
          .select('clerk_user_id')
          .eq('clerk_user_id', clerkUser.id)
          .maybeSingle();

        if (cancelled) return;

        if (error) {
          console.warn('AuthProvider: admin override lookup failed', error);
          setAdminOverride(false);
        } else {
          setAdminOverride(Boolean(data));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('AuthProvider: admin override fetch error', error);
          setAdminOverride(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clerkUser, supabase]);

  const fetchProfileDetails = useCallback(
    async (
      userId: string,
      defaults: { firstName: string; lastName: string; email?: string | null; phone?: string | null; avatarUrl?: string | null }
    ) => {
      // Try to fetch existing profile
      console.log('[AuthProvider.fetchProfileDetails] Attempting to fetch profile for userId:', userId);
      const profileSelect =
        'user_id, first_name, last_name, email, phone, birth_date, street_address, postal_code, city, created_at';
      const isClerkUser = userId.startsWith('user_');
      let shadowUuid: string | null = null;

      if (isClerkUser) {
        const { data: shadowUuidData, error: shadowUuidError } = await supabase.rpc('clerk_id_to_uuid', { clerk_id: userId });
        if (shadowUuidError) {
          console.warn('[AuthProvider.fetchProfileDetails] Failed to resolve shadow UUID via clerk_id_to_uuid', shadowUuidError);
        } else if (shadowUuidData) {
          shadowUuid = shadowUuidData as string;
          console.log('[AuthProvider.fetchProfileDetails] Resolved shadow UUID:', shadowUuid);
        }
      }
      
      // First, try querying by the provided userId (could be Clerk ID or shadow UUID)
      let { data, error } = await supabase
        .from('profiles_public_v')
        .select(profileSelect)
        .eq('user_id', userId)
        .maybeSingle();

      console.log('[AuthProvider.fetchProfileDetails] First query result:', {
        found: !!data,
        error: error?.message,
      });

      // If not found and we have a shadow UUID, also try querying by shadow UUID
      if (!data && shadowUuid) {
        console.log('[AuthProvider.fetchProfileDetails] First query failed. Trying with shadow UUID...');
        const result = await supabase
          .from('profiles_public_v')
          .select(profileSelect)
          .eq('user_id', shadowUuid)
          .maybeSingle();
        
        data = result.data;
        error = result.error;
        
        console.log('[AuthProvider.fetchProfileDetails] Shadow UUID query result:', {
          found: !!data,
          error: error?.message,
        });
      }

      console.log('[AuthProvider.fetchProfileDetails] Final result:', {
        found: !!data,
        phone: data?.phone,
        first_name: data?.first_name,
        last_name: data?.last_name,
      });

      // Ensure a shadow profile exists for Clerk users so event signups (FK on profiles) succeed
      let profileRecord = data;
      if (profileRecord && shadowUuid) {
        console.log('[AuthProvider.fetchProfileDetails] Checking shadow profile:', {
          profileRecordUserId: profileRecord.user_id,
          shadowUuid: shadowUuid,
          hasShadowProfile: profileRecord.user_id === shadowUuid,
        });
        
        const hasShadowProfile = profileRecord.user_id === shadowUuid;
        if (!hasShadowProfile) {
          console.log('[AuthProvider.fetchProfileDetails] Profile user_id does not match shadow UUID, checking if shadow profile exists...');
          
          const { data: shadowProfile, error: shadowProfileError } = await supabase
            .from('profiles_public_v')
            .select(profileSelect)
            .eq('user_id', shadowUuid)
            .maybeSingle();

          if (shadowProfileError) {
            console.warn('[AuthProvider.fetchProfileDetails] Failed checking for shadow profile entry', shadowProfileError);
          }

          console.log('[AuthProvider.fetchProfileDetails] Shadow profile query result:', {
            found: !!shadowProfile,
            error: shadowProfileError?.message,
          });

          if (shadowProfile) {
            console.log('[AuthProvider.fetchProfileDetails] Found existing shadow profile entry');
            profileRecord = shadowProfile;
          } else {
            console.log('[AuthProvider.fetchProfileDetails] Shadow profile missing — running upsert_user_profile to create it');
            const { data: upsertData, error: upsertError } = await supabase.rpc('upsert_user_profile', {
              p_clerk_id: userId,
              p_first_name: profileRecord.first_name || defaults.firstName,
              p_last_name: profileRecord.last_name || defaults.lastName || '',
              p_email: profileRecord.email ?? defaults.email ?? null,
              p_phone: profileRecord.phone ?? defaults.phone ?? null,
              p_avatar_url: (defaults as any).avatarUrl ?? null,
            });

            if (upsertError) {
              console.error('[AuthProvider.fetchProfileDetails] Failed to create shadow profile via upsert_user_profile', upsertError);
            } else {
              console.log('[AuthProvider.fetchProfileDetails] upsert_user_profile result:', upsertData);
              
              const { data: refreshedProfile, error: refreshError } = await supabase
                .from('profiles_public_v')
                .select(profileSelect)
                .eq('user_id', shadowUuid)
                .maybeSingle();

              console.log('[AuthProvider.fetchProfileDetails] Refreshed profile query result:', {
                found: !!refreshedProfile,
                error: refreshError?.message,
              });

              if (refreshedProfile) {
                console.log('[AuthProvider.fetchProfileDetails] Shadow profile created successfully');
                profileRecord = refreshedProfile;
              } else {
                console.error('[AuthProvider.fetchProfileDetails] Shadow profile was NOT created! Checking profiles table directly...');
                
                // Check if profile exists in base profiles table
                const { data: baseProfile, error: baseError } = await supabase
                  .from('profiles')
                  .select('user_id, first_name, last_name')
                  .eq('user_id', shadowUuid)
                  .maybeSingle();
                
                console.log('[AuthProvider.fetchProfileDetails] Base profiles table check:', {
                  found: !!baseProfile,
                  error: baseError?.message,
                  profile: baseProfile,
                });
              }
            }
          }
        } else {
          console.log('[AuthProvider.fetchProfileDetails] Profile user_id matches shadow UUID - all good!');
        }
      }

      if (error) {
        console.warn('AuthProvider: failed to load profile details from view', error);
      }

      if (profileRecord) {
        console.log('[AuthProvider.fetchProfileDetails] Profile found! Returning existing profile with phone:', profileRecord.phone);
        console.log('[AuthProvider.fetchProfileDetails] Address fields:', {
          birth_date: profileRecord.birth_date,
          street_address: profileRecord.street_address,
          postal_code: profileRecord.postal_code,
          city: profileRecord.city,
        });
        return {
          firstName: profileRecord.first_name || defaults.firstName,
          lastName: profileRecord.last_name || defaults.lastName,
          email: profileRecord.email ?? defaults.email ?? null,
          phone: profileRecord.phone ?? defaults.phone ?? null,
          birthDate: profileRecord.birth_date ?? null,
          streetAddress: profileRecord.street_address ?? null,
          postalCode: profileRecord.postal_code ?? null,
          city: profileRecord.city ?? null,
          createdAt: profileRecord.created_at ? new Date(profileRecord.created_at) : new Date(),
        };
      }

      // No profile found - create one for this Clerk user
      console.log('AuthProvider: No profile found for user', userId, '- creating one');
      
      try {
        const { data: insertData, error: insertError } = await supabase.rpc('upsert_user_profile', {
          p_clerk_id: userId,
          p_first_name: defaults.firstName,
          p_last_name: defaults.lastName || '',
          p_email: defaults.email,
          p_phone: defaults.phone,
          p_avatar_url: (defaults as any).avatarUrl ?? null,
        });

        if (insertError) {
          console.error('AuthProvider: Failed to create profile:', insertError);
        } else {
          console.log('AuthProvider: Successfully created profile for', userId, insertData);
          
          // If profile creation succeeded and we have a shadow UUID, fetch the created profile
          if (insertData && typeof insertData === 'object' && 'shadow_uuid' in insertData) {
            const createdShadowUuid = insertData.shadow_uuid as string;
            console.log('AuthProvider: Fetching newly created profile with shadow UUID:', createdShadowUuid);
            
            const { data: createdProfile } = await supabase
              .from('profiles_public_v')
              .select(profileSelect)
              .eq('user_id', createdShadowUuid)
              .maybeSingle();
            
            if (createdProfile) {
              console.log('AuthProvider: Successfully fetched newly created profile');
              return {
                firstName: createdProfile.first_name || defaults.firstName,
                lastName: createdProfile.last_name || defaults.lastName,
                email: createdProfile.email ?? defaults.email ?? null,
                phone: createdProfile.phone ?? defaults.phone ?? null,
                birthDate: createdProfile.birth_date ?? null,
                streetAddress: createdProfile.street_address ?? null,
                postalCode: createdProfile.postal_code ?? null,
                city: createdProfile.city ?? null,
                createdAt: createdProfile.created_at ? new Date(createdProfile.created_at) : new Date(),
              };
            }
          }
        }
      } catch (err) {
        console.error('AuthProvider: Error creating profile:', err);
      }

      // Return defaults as fallback
      return {
        firstName: defaults.firstName,
        lastName: defaults.lastName,
        email: defaults.email ?? null,
        phone: defaults.phone ?? null,
        birthDate: null,
        streetAddress: null,
        postalCode: null,
        city: null,
        createdAt: new Date(),
      };
    },
    [supabase]
  );

  useEffect(() => {
    let cancelled = false;

    const loadUser = async () => {
      if (!authLoaded || !userLoaded) {
        setAuthState((prev) => ({ ...prev, isLoading: true }));
        return;
      }

      if (isSignedIn && clerkUser) {
        setAuthState((prev) => ({ ...prev, isLoading: true }));

        const primaryEmail = clerkUser.primaryEmailAddress?.emailAddress || null;
        const baseFirst = clerkUser.firstName || clerkUser.fullName || primaryEmail?.split('@')[0] || 'Member';
        const baseLast = clerkUser.lastName || '';
        const basePhone = clerkUser.primaryPhoneNumber?.phoneNumber || null;
        const createdAt = clerkUser.createdAt ? new Date(clerkUser.createdAt) : new Date();
        const isAdmin = Boolean(
          adminOverride ||
          clerkUser.publicMetadata?.isAdmin ||
          clerkUser.publicMetadata?.role === 'admin'
        );

        const profile = await fetchProfileDetails(clerkUser.id, {
          firstName: baseFirst,
          lastName: baseLast,
          email: primaryEmail,
          phone: basePhone,
          avatarUrl: clerkUser.imageUrl || null,
        });

        if (cancelled) return;

        if (!profile.phone) {
          toast.warning(
            language === 'no'
              ? 'Legg inn telefonnummeret ditt for å fullføre profilen.'
              : 'Please add your phone number to complete your profile.'
          );
        }

        // For Clerk users, we need to get the shadow UUID for database operations
        // The profile creation function returns this in the response
        let effectiveUserId = clerkUser.id;
        
        // If this is a Clerk user (ID starts with 'user_'), we need to use the shadow UUID
        if (clerkUser.id.startsWith('user_')) {
          // Query the database to get the shadow UUID
          const { data: clerkIdMapping } = await supabase
            .rpc('clerk_id_to_uuid', { clerk_id: clerkUser.id });
          
          if (clerkIdMapping) {
            effectiveUserId = clerkIdMapping;
            console.log('AuthProvider: Using shadow UUID', effectiveUserId, 'for Clerk user', clerkUser.id);
          }
        }

        setAuthState({
          user: {
            id: effectiveUserId,  // Use shadow UUID for DB operations
            firstName: profile.firstName,
            lastName: profile.lastName ?? undefined,
            email: profile.email ?? undefined,
            phone: profile.phone ?? undefined,
            birthDate: profile.birthDate,
            streetAddress: profile.streetAddress,
            postalCode: profile.postalCode,
            city: profile.city,
            avatarUrl: clerkUser.imageUrl || null,
            isAdmin,
            createdAt: profile.createdAt || createdAt,
            groupIds: [],
            clerkUserId: clerkUser.id,  // Store original Clerk ID for reference
          },
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        setAuthState({ user: null, isAuthenticated: false, isLoading: false });
      }
    };

    void loadUser();

    return () => {
      cancelled = true;
    };
  }, [authLoaded, userLoaded, isSignedIn, clerkUser, adminOverride, fetchProfileDetails, language, supabase]);

  useEffect(() => {
    if (!authLoaded) {
      return;
    }

    // Only show login toast on actual login event, not on state re-evaluations
    if (isSignedIn && !wasSignedIn.current && hasShownInitialLoginToast.current) {
      toast.success(t('auth.login_success'));
    }

    if (!isSignedIn && wasSignedIn.current) {
      toast.success(t('auth.logout_success'));
    }

    // Mark that we've shown the initial toast after first load
    if (isSignedIn && !hasShownInitialLoginToast.current) {
      hasShownInitialLoginToast.current = true;
    }

    wasSignedIn.current = Boolean(isSignedIn);
  }, [authLoaded, isSignedIn, t]);

  const handleOpenSignIn = useCallback(async () => {
    await openSignIn({ afterSignInUrl: '/dashboard' });
    return true;
  }, [openSignIn]);

  const handleOpenSignUp = useCallback(async () => {
    await openSignUp({ afterSignUpUrl: '/dashboard' });
    return true;
  }, [openSignUp]);

  const handleSignOut = useCallback(async () => {
    await clerkSignOut();
  }, [clerkSignOut]);

  const handleUpdateProfile = useCallback(async ({
    firstName,
    lastName,
    phone,
    birthDate,
    streetAddress,
    postalCode,
    city,
  }: {
    firstName?: string;
    lastName?: string;
    phone: string;
    birthDate?: string | null;
    streetAddress?: string | null;
    postalCode?: string | null;
    city?: string | null;
  }): Promise<{ success: boolean; error?: string }> => {
    if (!clerkUser) {
      console.warn('updateProfile: no active Clerk user');
      return { success: false, error: 'No active user' };
    }

    if (!phone || !phone.trim()) {
      return { success: false, error: 'Phone number is required' };
    }

    console.log('[AuthProvider.updateProfile] Starting profile update for Clerk user:', clerkUser.id);
    console.log('[AuthProvider.updateProfile] Update payload:', { firstName, lastName, phone, birthDate, streetAddress, postalCode, city });

    try {
      if (firstName !== undefined || lastName !== undefined) {
        await clerkUser.update({
          firstName: firstName ?? clerkUser.firstName ?? '',
          lastName: lastName ?? clerkUser.lastName ?? '',
        });
      }

      console.log('[AuthProvider.updateProfile] Invoking manage-profile Edge Function...');
      
      const { data, error } = await supabase.functions.invoke('manage-profile', {
        body: {
          clerkUserId: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress ?? null,
          firstName,
          lastName,
          phone,
          birthDate,
          streetAddress,
          postalCode,
          city,
        },
      });

      console.log('[AuthProvider.updateProfile] Edge Function response:', {
        success: data?.success,
        error: error?.message || data?.error,
        profileReturned: !!data?.profile,
        phone: data?.profile?.phone,
      });

      if (error || !data?.success) {
        console.error('updateProfile: manage-profile function failed', error ?? data?.error);
        return { success: false, error: data?.error || (error?.message ?? 'Unable to update profile') };
      }

      const updated = data.profile as {
        user_id: string;
        first_name?: string | null;
        last_name?: string | null;
        email?: string | null;
        phone?: string | null;
        birth_date?: string | null;
        street_address?: string | null;
        postal_code?: string | null;
        city?: string | null;
      };

      await clerkUser.reload();

      console.log('[AuthProvider.updateProfile] Updating local state with new profile data. Phone:', updated.phone);

      setAuthState((prev) => {
        if (!prev.user) return prev;
        return {
          ...prev,
          user: {
            ...prev.user,
            firstName: updated.first_name || prev.user.firstName,
            lastName: updated.last_name || prev.user.lastName,
            email: updated.email ?? prev.user.email,
            phone: updated.phone ?? prev.user.phone,
            birthDate: updated.birth_date ?? null,
            streetAddress: updated.street_address ?? null,
            postalCode: updated.postal_code ?? null,
            city: updated.city ?? null,
            avatarUrl: clerkUser.imageUrl || prev.user.avatarUrl || null,
          },
        };
      });

      console.log('[AuthProvider.updateProfile] Profile update completed successfully! New phone:', updated.phone);
      return { success: true };
    } catch (error) {
      console.error('updateProfile: error updating profile', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, [clerkUser, supabase]);

  const contextValue: AuthContextType = useMemo<AuthContextType>(() => ({
    ...authState,
    signIn: async () => handleOpenSignIn(),
    signUp: async () => handleOpenSignUp(),
    signOut: handleSignOut,
    login: () => { void handleOpenSignIn(); },
    logout: handleSignOut,
    updateProfile: handleUpdateProfile,
  }), [authState, handleOpenSignIn, handleOpenSignUp, handleSignOut, handleUpdateProfile]);

  // Make auth context available globally for LanguageProvider
  useEffect(() => {
    (window as any).__authContext = authState;
  }, [authState]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
